/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/* generated using openapi-typescript-codegen -- do no edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ApiRequestOptions } from './ApiRequestOptions';
import { getValidAuthToken } from '@services/auth/auth';
import { getRequiredEnvVar } from '@shared/utils/validate-env';
import { generateAntiBotToken, generateAntiBotTokenWithReload, isRecaptchaDisabled, isSafeAntiBotMethod, AntiBotUnavailableError } from '@shared/utils/recaptcha';
import { awaitChallengeSettled } from '@shared/utils/antibot-challenge-stash';
import { CLEARANCE_HEADER, currentClearance } from '@shared/utils/antibot-clearance-store';

import { FF_MOCK_AUTH, FF_MOCK_CREDIT } from '@shared/utils/feature-flag';
import { useAuthSwitcherStore } from '@/features/dev-cockpit/services/auth-switcher';

type Resolver<T> = (options: ApiRequestOptions) => Promise<T>;
type Headers = Record<string, string>;

const REACT_APP_API_URL: string = getRequiredEnvVar('REACT_APP_API_URL');

export type OpenAPIConfig = {
    BASE: string;
    VERSION: string;
    WITH_CREDENTIALS: boolean;
    CREDENTIALS: 'include' | 'omit' | 'same-origin';
    TOKEN?: string | Resolver<string> | undefined;
    USERNAME?: string | Resolver<string> | undefined;
    PASSWORD?: string | Resolver<string> | undefined;
    HEADERS?: Headers | Resolver<Headers> | undefined;
    ENCODE_PATH?: ((path: string) => string) | undefined;
};

/**
 * Derives a contextual reCAPTCHA action string from a request's method and URL,
 * stripping path params and unsafe characters and capping length at 100 chars.
 *
 * @param options - The API request options; falls back to `api_request` when absent
 * @returns The normalized `method/path` action label
 */
function deriveActionFromRequest(options?: ApiRequestOptions): string {
    if (!options?.method || !options?.url) return 'api_request';
    const method = options.method.toLowerCase();
    const cleanUrl = options.url
        .replace(/\{[^}]+\}/g, '')
        .replace(/\/+/g, '/')
        .replace(/^\/|\/$/g, '')
        .replace(/[^A-Za-z/_]/g, '_');
    const action = `${method}/${cleanUrl}`.slice(0, 100);
    return action;
}

/**
 * Generate a fresh anti-bot token for every API request.
 * Uses a contextual action derived from the request method+URL to prevent
 * the reCAPTCHA SDK from returning cached tokens across different operations.
 *
 * Delegates entirely to generateAntiBotToken, which handles the disabled/mock
 * case by returning the bypass token instead of a real reCAPTCHA token.
 *
 * @returns The resolved token — bypass token when mocked, real token otherwise
 */
async function resolveAntiBotToken(options?: ApiRequestOptions): Promise<string> {
    const action = deriveActionFromRequest(options);
    return await generateAntiBotToken(action);
}

/**
 * Injects dev-only mock headers: an impersonation email under mock auth and a
 * mock credit band under mock credit, driven by feature flags.
 *
 * @param headers - The header map mutated in place
 */
function applyMockHeaders(headers: Record<string, string>): void {
    if (FF_MOCK_AUTH) {
        const impersonateEmail = useAuthSwitcherStore.getState().getActiveEmail();
        if (impersonateEmail) {
            headers['X-Impersonate-Email'] = impersonateEmail;
        }
    }
    if (FF_MOCK_CREDIT) {
        const band = sessionStorage.getItem('mock_credit_band') ?? 'Excelente';
        headers['X-Mock-Credit-Band'] = band;
    }
}

/**
 * Fallback anti-bot header resolution that reloads the reCAPTCHA SDK, used when
 * the initial token generation throws.
 *
 * Used to proceed without a token on failure -- the request would go out
 * missing `X-Anti-Bot`, the backend answers with an opaque 400, and the user
 * sees a generic failure with no indication it was a captcha problem. Fails
 * loudly instead, the same way the 403-retry path already does.
 *
 * @param headers - The header map to populate with `X-Anti-Bot` on success
 * @param options - The API request options used to derive the reCAPTCHA action
 * @throws {AntiBotUnavailableError} When no token could be obtained and
 *         reCAPTCHA is not disabled (mock/local-dev mode legitimately has none).
 */
async function applyAntiBotFallback(headers: Record<string, string>, options?: ApiRequestOptions): Promise<void> {
    const reloadHeaders = await resolveAntiBotHeadersWithReload(options);
    if (reloadHeaders['X-Anti-Bot']) {
        headers['X-Anti-Bot'] = reloadHeaders['X-Anti-Bot'];
        return;
    }
    if (isRecaptchaDisabled()) return;
    console.error('[ANTIBOT] Token generation failed after SDK reload — aborting request', {
        method: options?.method,
        url: options?.url,
    });
    throw new AntiBotUnavailableError('Could not obtain an anti-bot token after SDK reload.');
}

/**
 * Generates and attaches a fresh `X-Anti-Bot` token to the request headers,
 * falling back to an SDK-reload retry when the initial generation fails.
 *
 * @param headers - The header map to populate with `X-Anti-Bot`
 * @param options - The API request options used to derive the reCAPTCHA action
 */
async function applyAntiBotHeader(headers: Record<string, string>, options?: ApiRequestOptions): Promise<void> {
    applyAntiBotClearance(headers);
    try {
        const antiBotToken = await resolveAntiBotToken(options);
        if (antiBotToken) headers['X-Anti-Bot'] = antiBotToken;
    } catch {
        await applyAntiBotFallback(headers, options);
    }
}

/**
 * Echoes back the clearance a previously solved challenge earned, so a CHALLENGE
 * on this request is honoured instead of interrupting the same person again.
 *
 * Sent alongside `X-Anti-Bot`, never instead of it: the score check still runs
 * on every request, and a clearance only ever shortens how often someone is
 * asked to prove they are human.
 */
function applyAntiBotClearance(headers: Record<string, string>): void {
    const clearance = currentClearance();
    if (clearance) headers[CLEARANCE_HEADER] = clearance;
}

export const OpenAPIHousien: OpenAPIConfig = {
    BASE: REACT_APP_API_URL,
    VERSION: '1.0.0',
    WITH_CREDENTIALS: false,
    CREDENTIALS: 'include',
    TOKEN: async () => (await getValidAuthToken()) ?? '',
    USERNAME: undefined,
    PASSWORD: undefined,
    HEADERS: async (options: ApiRequestOptions) => {
        const headers: Record<string, string> = {};
        applyMockHeaders(headers);
        if (!isSafeAntiBotMethod(options?.method)) {
            // Don't mint a score token while a challenge is being solved --
            // queued requests would just pile up more 428s mid-overlay.
            await awaitChallengeSettled();
            await applyAntiBotHeader(headers, options);
        }
        return headers;
    },
    ENCODE_PATH: undefined,
};

/**
 * Resolve anti-bot headers after fully reloading the reCAPTCHA SDK.
 * Used exclusively by the anti-bot retry mechanism when a normal token
 * regeneration still fails (expired SDK session).
 */
export async function resolveAntiBotHeadersWithReload(options?: ApiRequestOptions): Promise<Record<string, string>> {
    if (isRecaptchaDisabled()) return {};
    const action = deriveActionFromRequest(options);
    const headers: Record<string, string> = {};
    try {
        const antiBotToken = await generateAntiBotTokenWithReload(action);
        if (antiBotToken) {
            headers['X-Anti-Bot'] = antiBotToken;
        }
    } catch {
        // If SDK reload and token generation fail, return empty headers
        // The retry will proceed without a fresh anti-bot token
    }
    return headers;
}

